/**
 * Blog Module - Halftone Theme
 * Handles reading progress, estimated read time, and other blog enhancements
 */

(function() {
  'use strict';

  /**
   * Reading Progress Bar
   * Tracks scroll position relative to article content
   */
  class ReadingProgress {
    constructor() {
      this.progressBar = document.querySelector('.reading-progress');
      this.progressFill = document.querySelector('.reading-progress__bar');
      this.articleContent = document.querySelector('[data-article-content]');

      if (!this.progressBar || !this.progressFill || !this.articleContent) {
        return;
      }

      this.header = document.querySelector('[data-header]');
      this.statusBar = null;
      this._resizeObserver = null;
      this._statusResizeObserver = null;
      this._mutationObserver = null;
      this.init();
    }

    init() {
      // Use passive scroll listener for performance
      window.addEventListener('scroll', this.handleScroll.bind(this), { passive: true });

      // Initial calculation
      this.handleScroll();

      // Recalculate on resize
      window.addEventListener('resize', this.debounce(this.handleScroll.bind(this), 100), { passive: true });

      // Offset progress bar below sticky header
      this.setupHeaderObservers();

      // Re-connect when the header section is updated in the theme editor
      document.addEventListener('shopify:section:load', (event) => {
        if (event.target.querySelector('[data-header]')) {
          this.teardownHeaderObservers();
          this.header = document.querySelector('[data-header]');
          this.setupHeaderObservers();
        }
      });
    }

    setupHeaderObservers() {
      // The bar pins to the top of the viewport, so it has to clear whatever
      // else is pinned there. The sticky status bar is a second pinned element
      // in the header's "none" and "smart" modes, where it is relocated out of
      // the header (see snippets/status-bar.liquid). In "always"/"reduce" it
      // rides inside the header, so the header's own height already covers it
      // and this query finds nothing.
      this.statusBar = document.querySelector('status-bar.status-bar--pinned');

      // The status bar's height can change after first paint (web fonts, a
      // currency switch reflowing the row), which would leave the offset stale.
      if (this.statusBar && window.ResizeObserver) {
        this._statusResizeObserver = new ResizeObserver(() => this.updateTopPosition());
        this._statusResizeObserver.observe(this.statusBar);
      }

      // A "none" header never moves, so it needs no observers — but a pinned
      // status bar above the bar still does, which is why the offset below is
      // computed either way.
      if (this.header && this.header.dataset.stickyBehavior !== 'none') {
        // ResizeObserver handles reduce mode (header shrinks on scroll)
        if (window.ResizeObserver) {
          this._resizeObserver = new ResizeObserver(() => this.updateTopPosition());
          this._resizeObserver.observe(this.header);
        }

        // MutationObserver handles smart mode (header--hidden class toggle)
        this._mutationObserver = new MutationObserver(() => this.updateTopPosition());
        this._mutationObserver.observe(this.header, {
          attributes: true,
          attributeFilter: ['class']
        });
      }

      this.updateTopPosition();
    }

    teardownHeaderObservers() {
      if (this._resizeObserver) {
        this._resizeObserver.disconnect();
        this._resizeObserver = null;
      }
      if (this._statusResizeObserver) {
        this._statusResizeObserver.disconnect();
        this._statusResizeObserver = null;
      }
      if (this._mutationObserver) {
        this._mutationObserver.disconnect();
        this._mutationObserver = null;
      }
    }

    updateTopPosition() {
      // Sum the heights of everything pinned above the bar. The header counts
      // only while it is sticky and on screen: "none" isn't sticky, and "smart"
      // slides it off (header--hidden), leaving just the status bar.
      let top = 0;
      if (
        this.header &&
        this.header.dataset.stickyBehavior !== 'none' &&
        !this.header.classList.contains('header--hidden')
      ) {
        top += this.header.offsetHeight;
      }

      // A relocated status bar is pinned in its own right, so its height stacks
      // on top of the header's. When it rides inside the header instead, this
      // reference is null and the header height already includes it.
      if (this.statusBar) {
        top += this.statusBar.offsetHeight;
      }

      this.progressBar.style.top = `${top}px`;
    }

    handleScroll() {
      const contentRect = this.articleContent.getBoundingClientRect();
      const contentTop = contentRect.top + window.scrollY;
      const contentHeight = this.articleContent.offsetHeight;
      const viewportHeight = window.innerHeight;

      // Start tracking when article comes into view
      const scrollStart = contentTop - viewportHeight * 0.2;
      // End when bottom of article is at middle of viewport
      const scrollEnd = contentTop + contentHeight - viewportHeight * 0.5;
      const scrollRange = scrollEnd - scrollStart;

      const currentScroll = window.scrollY;
      let progress = 0;

      if (currentScroll >= scrollStart && scrollRange > 0) {
        progress = Math.min(((currentScroll - scrollStart) / scrollRange) * 100, 100);
      }

      // Show/hide progress bar
      if (currentScroll > scrollStart - 100) {
        this.progressBar.classList.add('is-visible');
      } else {
        this.progressBar.classList.remove('is-visible');
      }

      // Update progress
      this.progressFill.style.width = `${progress}%`;
      this.progressBar.setAttribute('aria-valuenow', Math.round(progress));
    }

    debounce(func, wait) {
      let timeout;
      return function executedFunction(...args) {
        const later = () => {
          clearTimeout(timeout);
          func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
      };
    }
  }

  /**
   * Estimated Read Time Calculator
   * Calculates and displays estimated reading time based on word count
   */
  class ReadTimeCalculator {
    constructor() {
      this.WORDS_PER_MINUTE = 250; // Average reading speed
      this.articleContent = document.querySelector('[data-article-content]');
      this.readTimeDisplay = document.querySelector('[data-read-time]');

      if (!this.articleContent || !this.readTimeDisplay) {
        return;
      }

      this.init();
    }

    init() {
      const wordCount = this.countWords(this.articleContent.textContent);
      const readTime = Math.max(1, Math.ceil(wordCount / this.WORDS_PER_MINUTE));

      // Update display
      this.readTimeDisplay.textContent = this.formatReadTime(readTime);
      this.readTimeDisplay.setAttribute('data-word-count', wordCount);
    }

    countWords(text) {
      // Remove extra whitespace and count words
      return text
        .trim()
        .replace(/\s+/g, ' ')
        .split(' ')
        .filter(word => word.length > 0).length;
    }

    formatReadTime(minutes) {
      // Check for translation key, fallback to English
      const template = window.theme?.strings?.readTime || '{minutes} min read';
      return template.replace('{minutes}', minutes);
    }
  }

  /**
   * Drop Caps Enhancement
   * Adds drop cap class to first paragraph if enabled
   */
  class DropCaps {
    constructor() {
      this.articleContent = document.querySelector('[data-article-content]');
      // Check for data-drop-caps on the article element (closest ancestor with the attribute)
      this.isEnabled = this.articleContent?.closest('[data-drop-caps]') !== null;

      if (!this.articleContent || !this.isEnabled) {
        return;
      }

      this.init();
    }

    init() {
      // Find the first paragraph that has meaningful text
      const paragraphs = this.articleContent.querySelectorAll('p');

      for (const p of paragraphs) {
        const text = p.textContent.trim();

        // Skip empty paragraphs or those that start with special characters
        if (text.length > 50 && /^[A-Za-z]/.test(text)) {
          p.classList.add('has-drop-cap');
          this.fixOverflow(p);
          break;
        }
      }
    }

    // Some fonts have unusual cap-height metrics that cause initial-letter: 3
    // to push text onto a 4th line. After fonts load we sample line positions
    // using Range; if a gap is detected we reduce --drop-cap-initial-letter
    // so the drop cap stays within 3 lines.
    async fixOverflow(p) {
      const supportsInitialLetter =
        CSS.supports('initial-letter', '3') ||
        CSS.supports('-webkit-initial-letter', '3');
      if (!supportsInitialLetter) return;

      await document.fonts.ready;
      // Two frames: first lets the browser apply initial-letter layout,
      // second ensures measurement reflects the final paint geometry.
      await new Promise(r => requestAnimationFrame(r));
      await new Promise(r => requestAnimationFrame(r));

      const lineHeight = parseFloat(window.getComputedStyle(p).lineHeight);
      if (!lineHeight) return;

      // Walk to the first text node with enough content to sample 4 lines.
      const walker = document.createTreeWalker(p, NodeFilter.SHOW_TEXT);
      let textNode = null;
      while (walker.nextNode()) {
        if (walker.currentNode.textContent.length > 10) {
          textNode = walker.currentNode;
          break;
        }
      }
      if (!textNode) return;

      const range = document.createRange();
      const pTop = p.getBoundingClientRect().top;
      // Skip the first character — it's the drop cap, not part of the flow.
      const startOffset = textNode === p.firstChild ? 1 : 0;
      const lineYs = [];

      for (let i = startOffset; i < Math.min(textNode.length - 1, 250); i++) {
        range.setStart(textNode, i);
        range.setEnd(textNode, i + 1);
        const rects = range.getClientRects();
        if (!rects.length || rects[0].width < 1) continue;

        const y = Math.round(rects[0].top - pTop);
        if (lineYs.length === 0 || y > lineYs[lineYs.length - 1] + lineHeight * 0.4) {
          lineYs.push(y);
        }
        if (lineYs.length >= 4) break;
      }

      if (lineYs.length < 4) return;

      // Line 4 should sit at approximately lineYs[0] + 3 × lineHeight.
      // A gap larger than 15% of a line indicates the drop cap overflowed.
      const expectedLine4 = lineYs[0] + 3 * lineHeight;
      if (lineYs[3] > expectedLine4 + lineHeight * 0.15) {
        // Scale the current value down proportionally. Reading from the element
        // picks up the square style's 2.7 default or 3 for default/serif, so
        // the correction is always relative to whichever base is active.
        const currentIL = parseFloat(
          getComputedStyle(p).getPropertyValue('--drop-cap-initial-letter').trim()
        ) || 3;
        const ratio = expectedLine4 / lineYs[3];
        p.style.setProperty('--drop-cap-initial-letter', (currentIL * ratio).toFixed(2));
      }
    }
  }

  // Initialize modules when DOM is ready
  function init() {
    new ReadingProgress();
    new ReadTimeCalculator();
    new DropCaps();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
